import { useCallback, useState } from 'react';
import axios from 'axios';

import { getUserId, UserType } from '../accounts/UserType';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

type UserMode = 'profileId' | 'accountId';

export interface UserInputProps {
    onUserSelected: (user: UserType | null) => void;
    defaultValue?: UserType;
    disabled?: boolean;
}

export const UserInput = ({
    defaultValue,
    onUserSelected,
    disabled,
}: UserInputProps) => {
    const [mode, setMode] = useState<UserMode>(
        defaultValue?.type ?? 'profileId',
    );
    const [inputValue, setInputValue] = useState(
        defaultValue ? getUserId(defaultValue) : '',
    );
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleModeChange = (
        _: React.MouseEvent<HTMLElement>,
        newMode: UserMode | null,
    ) => {
        if (newMode) {
            setMode(newMode);
            setInputValue('');
            setError(null);
            onUserSelected(null);
        }
    };

    const handleProfileSearch = useCallback(async () => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;

        setIsSearching(true);
        setError(null);

        try {
            const res = await axios.get(`/api/accounts/v2/profiles/${trimmed}`);
            const profiles = res.data;

            if (!profiles || profiles.length === 0) {
                setError('No profile found with this ID.');
                return;
            }

            onUserSelected({ type: 'profileId', profileId: trimmed });
        } catch {
            setError('Profile not found or error verifying profile.');
        } finally {
            setIsSearching(false);
        }
    }, [inputValue, onUserSelected]);

    const handleAccountSelect = useCallback(() => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;

        onUserSelected({ type: 'accountId', accountId: trimmed });
    }, [inputValue, onUserSelected]);

    const handleSubmit = () => {
        if (mode === 'profileId') {
            handleProfileSearch();
        } else {
            handleAccountSelect();
        }
    };

    const isProfileMode = mode === 'profileId';
    const buttonLabel = isProfileMode ? 'Search' : 'Select';

    return (
        <Stack spacing={1.5}>
            <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleModeChange}
                size="small"
                disabled={disabled}
            >
                <ToggleButton value="profileId">Profile ID</ToggleButton>
                <ToggleButton value="accountId">Account ID</ToggleButton>
            </ToggleButtonGroup>

            <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'flex-start' }}
            >
                <TextField
                    label={isProfileMode ? 'Profile ID' : 'Account ID'}
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        setError(null);
                    }}
                    placeholder="e.g. 84ddd9dd-f3af-46b1-8fac-8241ff507e38"
                    size="small"
                    fullWidth
                    disabled={disabled || isSearching}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSubmit();
                    }}
                />
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={disabled || !inputValue.trim() || isSearching}
                >
                    {isSearching ? <CircularProgress size={20} /> : buttonLabel}
                </Button>
            </Stack>

            {!isProfileMode && (
                <Typography variant="caption" color="text.secondary">
                    Note: We don't verify whether an account with this ID
                    actually exists.
                </Typography>
            )}

            {isSearching && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}
        </Stack>
    );
};
